import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { getDocument, PDFDocumentProxy, RenderTask } from 'pdfjs-dist/webpack';
import PromptPdfCropDialog from './PromptPdfCropDialog';
import {
  PROMPT_MAX_PAGES_MESSAGE,
  PROMPT_UPLOAD_MAX_PAGES,
  PromptPdfCropRegion,
  assertClientUploadSize,
  createBottomTrimCropRegions,
  detectClientUploadKind,
  formatPageChipLabel,
  togglePageNumber,
  validateClientPageSelection,
} from './pdfPages';

export type PromptAttachedFile = {
  file: File;
  kind: 'pdf' | 'image';
  pageNumbers: number[];
  pageCount?: number;
  cropRegions?: PromptPdfCropRegion[];
};

const DEFAULT_CROP_PAGES = [1];

interface PromptFilePickerProps {
  attached: PromptAttachedFile | null;
  onChange: (next: PromptAttachedFile | null) => void;
  disabled?: boolean;
}

const PromptFilePicker: React.FC<PromptFilePickerProps> = ({ attached, onChange, disabled }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingPdf, setPendingPdf] = useState<File | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [selectedPages, setSelectedPages] = useState<number[]>([1]);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState<PromptAttachedFile | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const applyFile = useCallback(async (file: File) => {
    setError(null);
    try {
      const kind = detectClientUploadKind(file.name, file.type);
      assertClientUploadSize(file.size);
      if (kind === 'image') {
        setCropDialogOpen(false);
        setCropTarget(null);
        onChange({ file, kind, pageNumbers: [1] });
        return;
      }

      setLoading(true);
      const data = new Uint8Array(await file.arrayBuffer());
      const loaded = await getDocument({ data }).promise;
      const count = loaded.numPages;
      if (!count) {
        await loaded.destroy();
        throw new Error('Kunde inte läsa PDF-filen.');
      }
      if (count === 1) {
        await loaded.destroy();
        const next: PromptAttachedFile = {
          file,
          kind: 'pdf',
          pageNumbers: [1],
          pageCount: 1,
          cropRegions: createBottomTrimCropRegions([1]),
        };
        onChange(next);
        setCropTarget(next);
        setCropDialogOpen(true);
        return;
      }

      setPendingPdf(file);
      setPdfDocument(loaded);
      setPageCount(count);
      setSelectedPages([1]);
      setPageDialogOpen(true);
    } catch (err: any) {
      setError(err?.message || 'Kunde inte läsa filen.');
      onChange(null);
    } finally {
      setLoading(false);
      resetInput();
    }
  }, [onChange]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void applyFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (disabled || loading) return;
    const file = event.dataTransfer.files?.[0];
    if (file) {
      void applyFile(file);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled || loading) return;
    setDragActive(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled || loading) return;
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    setDragActive(false);
  };

  const openFilePicker = () => {
    if (disabled || loading) return;
    inputRef.current?.click();
  };

  const handleConfirmPages = () => {
    if (!pendingPdf) return;
    const validationError = validateClientPageSelection(selectedPages, pageCount);
    if (validationError) {
      setError(validationError);
      return;
    }
    const next: PromptAttachedFile = {
      file: pendingPdf,
      kind: 'pdf',
      pageNumbers: selectedPages,
      pageCount,
      cropRegions: createBottomTrimCropRegions(selectedPages),
    };
    onChange(next);
    setCropTarget(next);
    setPageDialogOpen(false);
    setPendingPdf(null);
    void pdfDocument?.destroy().catch(() => undefined);
    setPdfDocument(null);
    setError(null);
    setCropDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setPageDialogOpen(false);
    setPendingPdf(null);
    void pdfDocument?.destroy().catch(() => undefined);
    setPdfDocument(null);
    resetInput();
  };

  const handleSelectAll = () => {
    if (pageCount > PROMPT_UPLOAD_MAX_PAGES) {
      setError(PROMPT_MAX_PAGES_MESSAGE);
      return;
    }
    setSelectedPages(Array.from({ length: pageCount }, (_, index) => index + 1));
    setError(null);
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        hidden
        onChange={handleInputChange}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <UploadFileIcon />}
          onClick={openFilePicker}
          disabled={disabled || loading}
          sx={{ textTransform: 'none' }}
        >
          Ladda upp PDF eller bild
        </Button>
        <Box
          role="button"
          tabIndex={disabled || loading ? -1 : 0}
          aria-label="Släpp PDF, JPG eller PNG här"
          onClick={openFilePicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openFilePicker();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 168,
            height: 32,
            px: 1.25,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: dragActive ? '#1B3967' : '#c5cdd8',
            bgcolor: dragActive ? '#eef3fb' : '#fafbfc',
            color: dragActive ? '#1B3967' : 'text.secondary',
            cursor: disabled || loading ? 'default' : 'pointer',
            opacity: disabled || loading ? 0.6 : 1,
            userSelect: 'none',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {dragActive ? 'Släpp filen här' : 'Släpp PDF, JPG eller PNG'}
          </Typography>
        </Box>
        {attached && (
          <>
            <Chip
              label={`${attached.file.name} · ${formatPageChipLabel(attached.pageNumbers, Boolean(attached.cropRegions?.length))}`}
              onDelete={disabled ? undefined : () => {
                setCropDialogOpen(false);
                setCropTarget(null);
                onChange(null);
              }}
              deleteIcon={<CloseIcon />}
              size="small"
            />
            {attached.kind === 'pdf' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CropFreeIcon />}
                disabled={disabled}
                onClick={() => {
                  setCropTarget(attached);
                  setCropDialogOpen(true);
                }}
                sx={{ textTransform: 'none' }}
              >
                Beskär
              </Button>
            )}
          </>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        AI läser filen. För PDF kan du beskära bort tryckinformation längst ner. Flera sidor: välj sidor först.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Dialog open={pageDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Välj sidor att skicka till AI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pendingPdf?.name} · {pageCount} sidor. Standard är sida 1.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 1.5,
            }}
          >
            {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => (
              <PdfPageThumb
                key={pageNumber}
                pdfDocument={pdfDocument}
                pageNumber={pageNumber}
                selected={selectedPages.includes(pageNumber)}
                onToggle={() => setSelectedPages((current) => togglePageNumber(current, pageNumber))}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSelectAll}>Markera alla</Button>
          <Button onClick={handleCloseDialog}>Avbryt</Button>
          <Button variant="contained" onClick={handleConfirmPages} sx={{ bgcolor: '#1B3967' }}>
            Använd valda sidor
          </Button>
        </DialogActions>
      </Dialog>

      <PromptPdfCropDialog
        open={cropDialogOpen}
        file={cropTarget?.file || attached?.file || null}
        pageNumbers={cropTarget?.pageNumbers || attached?.pageNumbers || DEFAULT_CROP_PAGES}
        initialRegions={cropTarget?.cropRegions || attached?.cropRegions}
        onClose={() => setCropDialogOpen(false)}
        onSkip={() => {
          if (attached?.kind === 'pdf') {
            onChange({ ...attached, cropRegions: undefined });
          }
          setCropDialogOpen(false);
        }}
        onSave={(regions) => {
          const source = attached?.kind === 'pdf' ? attached : cropTarget;
          if (source?.kind === 'pdf') {
            onChange({
              ...source,
              cropRegions: regions.length ? regions : undefined,
            });
          }
          setCropDialogOpen(false);
        }}
      />
    </Box>
  );
};

interface PdfPageThumbProps {
  pdfDocument: PDFDocumentProxy | null;
  pageNumber: number;
  selected: boolean;
  onToggle: () => void;
}

const PdfPageThumb: React.FC<PdfPageThumbProps> = ({ pdfDocument, pageNumber, selected, onToggle }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    if (!pdfDocument) return undefined;
    let cancelled = false;

    const render = async () => {
      setRendering(true);
      try {
        const page = await pdfDocument.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 0.35, rotation: 0 });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;
        renderTaskRef.current?.cancel();
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const renderTask = page.render({
          canvasContext: context,
          viewport,
          canvas,
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
      } catch (error: any) {
        if (error?.name !== 'RenderingCancelledException') {
          // Keep the checkbox usable even if a thumbnail fails.
        }
      } finally {
        if (!cancelled) {
          setRendering(false);
        }
      }
    };

    void render();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [pageNumber, pdfDocument]);

  return (
    <Box
      onClick={onToggle}
      sx={{
        border: selected ? '2px solid #1B3967' : '1px solid #d0d5dd',
        borderRadius: 1,
        p: 1,
        cursor: 'pointer',
        bgcolor: selected ? '#eef3fb' : '#fff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Checkbox
          size="small"
          checked={selected}
          onClick={(event) => event.stopPropagation()}
          onChange={onToggle}
          sx={{ p: 0.25, mr: 0.5 }}
        />
        <Typography variant="caption">Sida {pageNumber}</Typography>
      </Box>
      <Box sx={{ position: 'relative', minHeight: 80, display: 'flex', justifyContent: 'center' }}>
        {rendering && (
          <CircularProgress size={18} sx={{ position: 'absolute', top: 24 }} />
        )}
        <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto' }} />
      </Box>
    </Box>
  );
};

export default PromptFilePicker;
